import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import settingsService from '../../../../../services/settings.service';
import usersService from '../../../../../services/users.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../../utils/ecxSwal';
import SubHeader from '../../../../layout/components/SubHeader';
import { ClinicInfoPanel } from '../../clinicsettings/info/clinicInfoParts';
import ClinicSettingsCancelButton from '../../clinicsettings/subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../../clinicsettings/subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../../clinicsettings/subparts/ClinicSettingsListSkeleton';
import {
    CHANNEL_KEYS,
    REMINDER_CATEGORIES,
    ReminderOverviewStats,
    ReminderSettingsPanels,
    TOGGLE_KEYS,
    getDefaultReminderSettings,
    getEcpDisplayName,
    normalizeSettings,
    stripToReminderPayload,
} from '../reminderSettingsShared';

const ReminderCard = () => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [searchParams] = useSearchParams();
    const [settings, setSettings] = useState(null);
    const [selectedScope, setSelectedScope] = useState('');
    const [ecpUsers, setEcpUsers] = useState([]);
    const [ecpLoading, setEcpLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [settingsMissing, setSettingsMissing] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        reset,
        watch,
        formState: { errors },
    } = useForm();

    const formValues = watch();

    const selectedEcp = useMemo(
        () => ecpUsers.find((ecp) => ecp.id === selectedScope) || null,
        [ecpUsers, selectedScope]
    );

    const scopeLabel = getEcpDisplayName(selectedEcp);
    const scopeSubtitle = selectedScope
        ? `Manage reminder settings for ${scopeLabel}`
        : 'Select an ECP to manage reminder settings';

    const fetchEcpUsers = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        setEcpLoading(true);

        try {
            const response = await settingsService.getUsersByRole(clinicId, 'ECP');
            setEcpUsers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error(error);
            setEcpUsers([]);
            showError({
                title: 'Load Failed',
                text: 'Unable to load ECP list. Please try again.',
            });
        } finally {
            setEcpLoading(false);
        }
    }, [clinicId]);

    const fetchSettings = useCallback(async (userId) => {
        if (!clinicId || !userId) {
            setSettings(null);
            setIsLoading(false);
            setLoadFailed(false);
            setSettingsMissing(false);
            return;
        }

        setIsLoading(true);
        setLoadFailed(false);
        setSettingsMissing(false);

        try {
            const response = await usersService.getUserReminder(clinicId, userId);
            const normalized = normalizeSettings(response.data);
            setSettings(normalized);
            reset(normalized);
        } catch (error) {
            console.error(error);

            if (error?.response?.status === 404) {
                const defaults = getDefaultReminderSettings();
                setSettings(defaults);
                reset(defaults);
                setSettingsMissing(true);
            } else {
                setLoadFailed(true);
                showError({
                    title: 'Load Failed',
                    text: 'Unable to load ECP reminder settings. Please try again.',
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, [clinicId, reset]);

    useEffect(() => {
        fetchEcpUsers();
    }, [fetchEcpUsers]);

    useEffect(() => {
        if (ecpLoading) {
            return;
        }

        const ecpIdFromQuery = searchParams.get('ecp');
        const hasQueryEcp = ecpIdFromQuery && ecpUsers.some((ecp) => ecp.id === ecpIdFromQuery);
        const hasSelectedEcp = selectedScope && ecpUsers.some((ecp) => ecp.id === selectedScope);

        if (hasQueryEcp) {
            if (selectedScope !== ecpIdFromQuery) {
                setSelectedScope(ecpIdFromQuery);
            }
            return;
        }

        if (hasSelectedEcp) {
            return;
        }

        setSelectedScope(ecpUsers[0]?.id || '');
    }, [ecpLoading, ecpUsers, searchParams, selectedScope]);

    useEffect(() => {
        setIsEditing(false);
        fetchSettings(selectedScope);
    }, [selectedScope, fetchSettings]);

    const allItems = useMemo(
        () => REMINDER_CATEGORIES.flatMap((category) => category.items),
        []
    );

    const currentValues = isEditing ? formValues : settings;

    const enabledCount = useMemo(() => {
        if (!currentValues) {
            return 0;
        }

        return TOGGLE_KEYS.filter((key) => currentValues[key]).length;
    }, [currentValues]);

    const activeChannels = useMemo(() => {
        if (!currentValues) {
            return 0;
        }

        return CHANNEL_KEYS.filter((key) => currentValues[key]).length;
    }, [currentValues]);

    const handleEdit = () => {
        reset(settings || getDefaultReminderSettings());
        setIsEditing(true);
    };

    const handleCancel = () => {
        reset(settings || getDefaultReminderSettings());
        setIsEditing(false);
    };

    const handleScopeChange = (nextScope) => {
        if (isEditing) {
            setIsEditing(false);
        }

        setSelectedScope(nextScope);
    };

    const onSubmit = async (data) => {
        if (!selectedScope) {
            return;
        }

        setIsSaving(true);

        try {
            const response = await usersService.updateUserReminder(
                clinicId,
                selectedScope,
                stripToReminderPayload(data)
            );

            const normalized = normalizeSettings(response.data);
            setSettings(normalized);
            reset(normalized);
            setSettingsMissing(false);
            setIsEditing(false);

            await showSuccess({
                title: 'Settings Saved',
                text: `${scopeLabel}'s reminder settings were updated successfully.`,
            });
        } catch (error) {
            console.error(error);
            showError({
                title: 'Save Failed',
                text: 'Unable to update reminder settings. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const showHeaderActions = Boolean(selectedScope && settings && !loadFailed);

    const headerActions = showHeaderActions ? (
        <div className="d-flex flex-wrap align-items-center justify-content-md-end gap-2">
            <span className="ecx-page-chip">
                <i className="bi bi-person-badge"></i>
                {scopeLabel}
            </span>
            <span className="ecx-page-chip">
                <i className="bi bi-grid-3x3-gap"></i>
                {allItems.length} settings
            </span>
            <span className="ecx-page-chip">
                <i className="bi bi-check-circle"></i>
                {enabledCount} enabled
            </span>
            {isEditing ? (
                <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
            ) : (
                <button
                    type="button"
                    className="btn btn-sm btn-light-primary rounded-pill"
                    onClick={handleEdit}
                >
                    <i className="bi bi-pencil me-1"></i>
                    {settingsMissing ? 'Create Settings' : 'Edit Settings'}
                </button>
            )}
        </div>
    ) : null;

    const pageHeader = (
        <SubHeader
            title="Clinic Reminders"
            description={scopeSubtitle}
            icon="bi-bell"
            containerClass="w-100 px-4"
        >
            {headerActions}
        </SubHeader>
    );

    const overviewStats = (
        <ReminderOverviewStats
            allItemsCount={settings ? allItems.length : 0}
            enabledCount={settings ? enabledCount : 0}
            activeChannels={settings ? activeChannels : 0}
            selectedScope={selectedScope}
            ecpUsers={ecpUsers}
            ecpLoading={ecpLoading}
            isEditing={isEditing}
            isSaving={isSaving}
            onScopeChange={handleScopeChange}
        />
    );

    if (ecpLoading || (selectedScope && isLoading)) {
        return (
            <>
                {pageHeader}
                <div className="ecx-page-body">
                    {overviewStats}
                    {REMINDER_CATEGORIES.map((category) => (
                        <ClinicInfoPanel key={category.title} title={category.title} icon={category.icon}>
                            <ClinicSettingsListSkeleton rows={category.items.length} />
                        </ClinicInfoPanel>
                    ))}
                </div>
            </>
        );
    }

    if (!ecpLoading && ecpUsers.length === 0) {
        return (
            <>
                {pageHeader}
                <div className="ecx-page-body">
                    {overviewStats}
                    <div className="alert alert-warning mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        No ECP users found. Add an ECP before configuring reminder settings.
                    </div>
                </div>
            </>
        );
    }

    if (loadFailed) {
        return (
            <>
                {pageHeader}
                <div className="ecx-page-body">
                    {overviewStats}
                    <div className="alert alert-danger d-flex align-items-center justify-content-between gap-3">
                        <span>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            Failed to load reminder settings.
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger rounded-pill"
                            onClick={() => fetchSettings(selectedScope)}
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Retry
                        </button>
                    </div>
                </div>
            </>
        );
    }

    if (!settings) {
        return (
            <>
                {pageHeader}
                <div className="ecx-page-body">{overviewStats}</div>
            </>
        );
    }

    return (
        <>
            {pageHeader}
            <div className="ecx-page-body">
                {overviewStats}

                {settingsMissing && !isEditing && (
                    <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3">
                        <span>
                            <i className="bi bi-info-circle me-2"></i>
                            No reminder settings found for {scopeLabel}. Create settings to get started.
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-warning rounded-pill"
                            onClick={handleEdit}
                        >
                            <i className="bi bi-plus-circle me-1"></i>
                            Create Settings
                        </button>
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="reminder-settings-form">
                    <ReminderSettingsPanels
                        values={currentValues}
                        isEditing={isEditing}
                        register={register}
                        control={control}
                        errors={errors}
                        fieldIdPrefix={selectedScope}
                    />

                    {isEditing && (
                        <div className="reminder-settings-form__footer">
                            <ClinicSettingsFormFooter
                                isSaving={isSaving}
                                submitLabel={settingsMissing ? 'Create Settings' : 'Save Changes'}
                            />
                        </div>
                    )}
                </form>
            </div>
        </>
    );
};

export default ReminderCard;
